import React, { useState, useCallback, useMemo } from "react";
import { TEXT } from "Clients/ACCB/ACCBTheme";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Collapse,
  Container,
  Divider,
  Grid,
  IconButton,
  ListItemText,
  MenuItem,
  Paper,
  TextField,
  Tooltip,
  Typography,
  makeStyles,
} from "@material-ui/core";
import AddIcon from "@material-ui/icons/Add";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import ErrorOutlineIcon from "@material-ui/icons/ErrorOutline";

import DocumentUpload from "features/MediationForm/DocumentUpload";
import ThankYouACCB from "./ThankYouACCB";
// import { submitMediationRequest } from "features/MediationForm/mediationApi";
import { FIELD_TYPES } from "features/MediationForm/validationSchema";
import {
  CASE_CATEGORIES,
  caseCategorySection,
  claimantFieldTemplate,
  claimantCounselFieldTemplate,
  respondentFieldTemplate,
  respondentCounselFieldTemplate,
  documentUploadField,
  mediationDetailsSection,
  getDocumentUploadMeta,
  blankClaimant,
  blankClaimantCounsel,
  blankRespondent,
  blankRespondentCounsel,
  buildPartyOptions,
  MULTI_SELECT,
} from "./formSchema";
import instance from "api/globalaxios";
import { getPlatform } from "slices/disputeSlice";
import useCountries from "../hooks/useCountries";
import useLocations from "../hooks/useLocations";
import { CONFLICT_FIELD_MAP, CONFLICT_MESSAGES } from "features/MediationForm/mediationApi";

// ─────────────────────────────────────────────────────────────────────────────
// Auto-fill source
// ─────────────────────────────────────────────────────────────────────────────
// Auto-fill reads one source document and prefills the whole form. 
const AUTOFILL_SOURCE = {
  label: "Section 11",
  documentName: "petition / court order",
  endpoint: "bac/register-case/extract-section11/",
  accept: ".pdf",
};

const AUTOFILL_UPLOAD_FIELD = {
  accept: AUTOFILL_SOURCE.accept,
  multiple: false,
  dropLabel: "Drag & drop the document here, or",
  helperText: `${AUTOFILL_SOURCE.label} ${AUTOFILL_SOURCE.documentName} — PDF, up to 10MB`,
};

// ─────────────────────────────────────────────────────────────────────────────
// Styles
// ─────────────────────────────────────────────────────────────────────────────
const useStyles = makeStyles((theme) => ({
  page: {
    minHeight: "100vh",
    backgroundColor: theme.palette.background.default,
    paddingTop: theme.spacing(3),
    paddingBottom: theme.spacing(6),
  },
  container: {
    maxWidth: 1180,
    paddingLeft: 0,
    paddingRight: 0,
  },
  paper: {
    borderRadius: 0,
    boxShadow: "none",
    border: `1px solid ${theme.palette.divider}`,
    overflow: "hidden",
    textAlign: "left",
  },
  formHeader: {
    padding: theme.spacing(4, 4, 3),
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  formLabel: {
    fontSize: "0.6875rem",
    fontWeight: 700,
    letterSpacing: "0.12em",
    textTransform: "uppercase",
    color: theme.palette.primary.main,
    marginBottom: theme.spacing(1),
  },
  formSubtitle: {
    fontSize: "0.875rem",
    color: theme.palette.text.secondary,
  },
  formBody: { padding: theme.spacing(4) },
  // Two columns halve the page height, so the gaps that separated sections on a
  // very long page are now oversized relative to the content between them.
  sectionColumns: {
    marginTop: theme.spacing(1),
  },
  sectionWrapper: {
    marginBottom: theme.spacing(4),
    "&:last-child": { marginBottom: 0 },
  },
  sectionHeader: { marginBottom: theme.spacing(3) },
  sectionIndex: {
    fontSize: "0.6875rem",
    fontWeight: 700,
    letterSpacing: "0.1em",
    textTransform: "uppercase",
    color: theme.palette.primary.main,
    marginBottom: theme.spacing(0.75),
  },
  sectionTitle: {
    fontSize: "1.0625rem",
    fontWeight: 700,
    color: theme.palette.text.primary,
    letterSpacing: "-0.01em",
    lineHeight: 1.3,
  },
  sectionSubtitle: {
    fontSize: "0.8125rem",
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(0.5),
    lineHeight: 1.55,
  },
  sectionDivider: {
    margin: theme.spacing(4, 0),
    backgroundColor: theme.palette.divider,
  },
  sectionHr: {
    marginBottom: theme.spacing(3),
    backgroundColor: theme.palette.divider,
  },
  partyCard: {
    border: `1px solid ${theme.palette.divider}`,
    padding: theme.spacing(3),
    marginBottom: theme.spacing(3),
    position: "relative",
    backgroundColor: theme.palette.background.paper,
  },
  partyCardHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: theme.spacing(2.5),
  },
  partyCardLabel: {
    fontSize: "0.8125rem",
    fontWeight: 700,
    color: theme.palette.text.secondary,
    letterSpacing: "0.04em",
  },
  removePartyBtn: {
    color: theme.palette.error.main,
    padding: 4,
  },
  addPartyBtn: {
    fontSize: "0.875rem",
    marginTop: theme.spacing(1),
  },
  textField: {
    "& .MuiInputBase-root": { fontSize: "0.9375rem", borderRadius: 0 },
    "& .MuiInputLabel-root": { fontSize: "0.9375rem" },
    "& .MuiOutlinedInput-root": {
      "& fieldset": { borderColor: theme.palette.divider },
      "&:hover fieldset": { borderColor: theme.palette.text.secondary },
      "&.Mui-focused fieldset": {
        borderColor: theme.palette.primary.main,
        borderWidth: 1,
      },
    },
  },
  helperText: { fontSize: "0.75rem", marginTop: theme.spacing(0.5) },
  multiSelectPlaceholder: { color: theme.palette.text.secondary },
  footer: {
    padding: theme.spacing(3, 4),
    borderTop: `1px solid ${theme.palette.divider}`,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexWrap: "wrap",
    gap: theme.spacing(2),
  },
  submitButton: {
    fontWeight: 700,
    fontSize: "0.875rem",
    letterSpacing: "0.05em",
    textTransform: "uppercase",
    padding: theme.spacing(1.25, 4),
    borderRadius: 0,
    minWidth: 180,
    position: "relative",
  },
  buttonProgress: {
    position: "absolute",
    top: "50%",
    left: "50%",
    marginTop: -10,
    marginLeft: -10,
  },
  requiredNote: {
    fontSize: "0.75rem",
    color: theme.palette.text.secondary,
  },
  globalError: {
    display: "flex",
    alignItems: "flex-start",
    gap: "10px",
    padding: theme.spacing(2, 2.5),
    border: `1px solid ${theme.palette.error.light}`,
    backgroundColor: "rgba(211, 47, 47, 0.04)",
    marginBottom: theme.spacing(3),
  },
  globalErrorIcon: {
    color: theme.palette.error.main,
    fontSize: 20,
    marginTop: 1,
    flexShrink: 0,
  },
  globalErrorText: { color: theme.palette.error.main },
  autofillCard: {
    border: `1px solid ${theme.palette.divider}`,
    backgroundColor: "#faf7fc",
    padding: theme.spacing(3),
    marginBottom: theme.spacing(4),
  },
  autofillTitle: {
    fontSize: "0.9375rem",
    fontWeight: 700,
    color: theme.palette.text.primary,
  },
  autofillSubtitle: {
    fontSize: "0.8125rem",
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(0.5),
    marginBottom: theme.spacing(2),
    lineHeight: 1.55,
  },
  autofillStatus: {
    fontSize: "0.8125rem",
    fontWeight: 500,
    marginTop: theme.spacing(1.5),
    lineHeight: 1.5,
  },
  autofillStatusSuccess: { color: TEXT.success },
  autofillStatusError: { color: theme.palette.error.main },
  autofillButton: {
    marginTop: theme.spacing(2),
  },
}));

// At the md breakpoint and up the form is wide enough for three columns, which
// is what actually shortens the page - sm:6 on a wider container just makes two
// fat columns. The schema only declares xs/sm, so derive md from it here rather
// than annotating all 32 fields: a half-width field becomes a third, and
// anything already full width (textareas, addresses) stays full width.
const gridPropsFor = (field) => {
  const g = field.gridSize || {};
  const sm = g.sm;
  return {
    xs: g.xs || 12,
    sm,
    md: g.md || (sm && sm <= 6 ? 4 : undefined),
  };
};

// ─────────────────────────────────────────────────────────────────────────────
// Validation helpers
// ─────────────────────────────────────────────────────────────────────────────
const validateMobile = (val, required) => {
  if (!val || !val.toString().trim()) return required ? "Phone Number is required" : "";
  return "";
};

const validateEmail = (val) => {
  if (!val) return "";
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val)) return "Please enter a valid email address";
  return "";
};

const validateRequired = (val, label) => {
  if (!val || !val.toString().trim()) return `${label} is required`;
  return "";
};

// Helper utility to safely pull out terminal identifier names from compound schema paths
const getCleanFieldName = (fieldId) => {
  if (!fieldId) return "";
  return fieldId.includes(".") ? fieldId.split(".").slice(1).join(".") : fieldId;
};

// ─────────────────────────────────────────────────────────────────────────────
// PartyField — single controlled field (no Formik)
// ─────────────────────────────────────────────────────────────────────────────
const PartyField = ({ field, value, onChange, siblingValues, fieldErrors }) => {
  const classes = useStyles();

  if (field.dependsOn) {
    const depKey = getCleanFieldName(field.dependsOn.field);
    if ((siblingValues || {})[depKey] !== field.dependsOn.value) return null;
  }

  const cleanFieldId = getCleanFieldName(field.id);
  const fieldError = (fieldErrors || {})[cleanFieldId] || "";

  const commonProps = {
    id: field.id,
    label: field.label,
    required: field.required,
    value: value || "",
    onChange: (e) => onChange(field.id, e.target.value),
    error: Boolean(fieldError),
    helperText: fieldError || field.helperText || "",
    variant: "outlined",
    fullWidth: true,
    size: "medium",
    className: classes.textField,
    FormHelperTextProps: { className: classes.helperText },
  };

  if (field.type === MULTI_SELECT) {
    const options = field.options || [];
    const selected = Array.isArray(value) ? value : [];
    const labelFor = (val) => {
      const match = options.find((opt) => opt.value === val);
      return match ? match.label : val;
    };

    return (
      <TextField
        {...commonProps}
        select
        value={selected}
        onChange={(e) => onChange(field.id, e.target.value)}
        InputLabelProps={{ shrink: true }}
        SelectProps={{
          multiple: true,
          displayEmpty: true,
          renderValue: (sel) =>
            !sel || sel.length === 0 ? (
              <span className={classes.multiSelectPlaceholder}>
                {field.placeholder || "Select"}
              </span>
            ) : (
              sel.map(labelFor).join(", ")
            ),
        }}
      >
        {options.map((opt) => (
          <MenuItem key={opt.value} value={opt.value}>
            <Checkbox color="primary" checked={selected.indexOf(opt.value) > -1} />
            <ListItemText primary={opt.label} />
          </MenuItem>
        ))}
      </TextField>
    );
  }

  if (field.type === FIELD_TYPES.SELECT) {
    // Unlike the MULTI_SELECT branch above, this one passed no displayEmpty or
    // renderValue, so an unchosen select rendered as a blank box with nothing to
    // say what belonged in it.
    // Prefixing the label reads badly for anything that is not a plain noun -
    // "Is Claimant an Organisation?" became "Select Is Claimant an Organisa...".
    // Matches the MULTI_SELECT branch; set `placeholder` in the schema when a
    // field wants a richer prompt.
    const placeholder = field.placeholder || "Select";
    return (
      <TextField
        {...commonProps}
        select
        InputLabelProps={{ shrink: true }}
        SelectProps={{
          displayEmpty: true,
          renderValue: (selected) => {
            if (selected === undefined || selected === null || selected === "") {
              return (
                <span className={classes.multiSelectPlaceholder}>
                  {placeholder}
                </span>
              );
            }
            const match = (field.options || []).find((o) => o.value === selected);
            return match ? match.label : selected;
          },
        }}
      >
        {(field.options || []).map((opt) => (
          <MenuItem key={opt.value} value={opt.value}>
            {opt.label}
          </MenuItem>
        ))}
      </TextField>
    );
  }

  return (
    <TextField
      {...commonProps}
      type={field.type}
      placeholder={field.placeholder}
      multiline={field.type === FIELD_TYPES.TEXTAREA}
      rows={field.rows || undefined}
    />
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// PartyCard — one entry in a repeatable section
// ─────────────────────────────────────────────────────────────────────────────
const PartyCard = ({ label, index, fieldTemplate, templateOptions, values, onChange, onRemove, canRemove, errors }) => {
  const classes = useStyles();
  const countries = useCountries();

  // Find country object to get its country code (e.g. "IN")
  const selectedCountry = (countries || []).find((c) => c.id === (values || {}).country);
  const countryCode = selectedCountry ? selectedCountry.code : "";
  const stateCode = (values || {}).state_code || "";

  // Call hook to fetch states and cities
  const { states, cities } = useLocations(countryCode, stateCode);

  const fields = fieldTemplate(index, templateOptions);

  // Map state and city options dynamically
  const updatedFields = fields.map((field) => {
    const key = getCleanFieldName(field.id);
    if (key === "state_code") {
      return {
        ...field,
        options: (states || []).map((s) => ({ value: s.iso2, label: s.name })),
      };
    }
    if (key === "city") {
      return {
        ...field,
        options: (cities || []).map((c) => ({ value: c.name, label: c.name })),
      };
    }
    return field;
  });

  const handleChange = (fieldId, val) => {
    const key = getCleanFieldName(fieldId);
    if (key === "country") {
      onChange(index, "country", val);
      onChange(index, "state", "");
      onChange(index, "state_code", "");
      onChange(index, "city", "");
    } else if (key === "state_code") {
      const selectedStateObj = (states || []).find((s) => s.iso2 === val);
      onChange(index, "state", selectedStateObj ? selectedStateObj.name : "");
      onChange(index, "state_code", val);
      onChange(index, "city", "");
    } else {
      onChange(index, key, val);
    }
  };

  return (
    <Box className={classes.partyCard}>
      <Box className={classes.partyCardHeader}>
        <Typography className={classes.partyCardLabel}>
          {label} {index + 1}
        </Typography>
        {canRemove && (
          <Tooltip title={`Remove ${label} ${index + 1}`}>
            <IconButton
              className={classes.removePartyBtn}
              onClick={() => onRemove(index)}
              size="small"
            >
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Box>
      <Grid container spacing={3}>
        {updatedFields.map((field) => {
          let hide = false;
          if (field.dependsOn) {
            const depKey = getCleanFieldName(field.dependsOn.field);
            if (values[depKey] !== field.dependsOn.value) hide = true;
          }
          if (hide) return null;
          const fieldKey = getCleanFieldName(field.id);
          return (
            <Grid item key={field.id} {...gridPropsFor(field)}>
              <PartyField
                field={field}
                value={values[fieldKey]}
                onChange={handleChange}
                siblingValues={values}
                fieldErrors={errors}
              />
            </Grid>
          );
        })}
      </Grid>
    </Box>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// MultiPartySection — repeatable section with Add / Remove
// ─────────────────────────────────────────────────────────────────────────────
const MultiPartySection = ({ sectionNumber, sectionTitle, sectionSubtitle, partyLabel, addLabel, parties, fieldTemplate, templateOptions, onAdd, onRemove, onChange, errors }) => {
  const classes = useStyles();
  return (
    <Box className={classes.sectionWrapper}>
      <Box className={classes.sectionHeader}>
        <Typography className={classes.sectionIndex}>{String(sectionNumber).padStart(2, "0")}</Typography>
        <Typography variant="h6" className={classes.sectionTitle}>{sectionTitle}</Typography>
        {sectionSubtitle && <Typography className={classes.sectionSubtitle}>{sectionSubtitle}</Typography>}
      </Box>
      <Divider className={classes.sectionHr} />
      {parties.map((partyValues, idx) => (
        <PartyCard
          key={partyValues.id || idx}
          label={partyLabel}
          index={idx}
          fieldTemplate={fieldTemplate}
          templateOptions={templateOptions}
          values={partyValues}
          onChange={onChange}
          onRemove={onRemove}
          canRemove={parties.length > 1}
          errors={errors[idx] || {}}
        />
      ))}
      <Button variant="outlined" color="primary" size="small" startIcon={<AddIcon />} className={classes.addPartyBtn} onClick={onAdd} disableElevation>
        {addLabel}
      </Button>
    </Box>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Country and state the referring MSEFC councils sit in. Fixed: the Centre
// only takes references from Karnataka's councils.
const REFERRING_COUNTRY_CODE = "IN";
const REFERRING_STATE_CODE = "KA";

// ─────────────────────────────────────────────────────────────────────────────
// StaticSection — flat controlled fields (category + mediation details)
// ─────────────────────────────────────────────────────────────────────────────
const StaticSection = ({ sectionNumber, section, values, onChange, errors }) => {
  const classes = useStyles();
  return (
    <Box className={classes.sectionWrapper}>
      <Box className={classes.sectionHeader}>
        <Typography className={classes.sectionIndex}>{String(sectionNumber).padStart(2, "0")}</Typography>
        <Typography variant="h6" className={classes.sectionTitle}>{section.sectionTitle}</Typography>
        {section.sectionSubtitle && <Typography className={classes.sectionSubtitle}>{section.sectionSubtitle}</Typography>}
      </Box>
      <Divider className={classes.sectionHr} />
      <Grid container spacing={3}>
        {section.fields.map((field) => {
          const cleanFieldId = getCleanFieldName(field.id);

          if (field.dependsOn) {
            const depKey = getCleanFieldName(field.dependsOn.field);
            if (values[depKey] !== field.dependsOn.value) return null;
          }
          return (
            <Grid item key={field.id} {...gridPropsFor(field)}>
              <PartyField
                field={field}
                value={values[cleanFieldId]}
                onChange={(id, val) => onChange(getCleanFieldName(id), val)}
                siblingValues={values}
                fieldErrors={errors}
              />
            </Grid>
          );
        })}
      </Grid>
    </Box>
  );
};

const serialiseParty = (party, orgKey, countries = []) => {
  const isOrganisation = party[orgKey] === "true" || party[orgKey] === true;
  // The Country select holds the id from /api/country/; send the id and the
  // matching display name alongside it.
  const selectedCountry = countries.find((c) => c.id === party.country);
  return {
    id: party.id,
    name: party.name,
    email: party.email,
    mobile: party.mobile,
    address: party.address,
    city: party.city,
    state: party.state,
    state_code: party.state_code,
    region: party.state,
    pincode: party.pincode,
    country_id: party.country || null,
    country: selectedCountry ? selectedCountry.name : "",
    // An individual represents themselves; only an organisation names a separate
    // representative (the field is hidden for individuals).
    representative_name: isOrganisation ? party.representative_name || "" : party.name,
    [orgKey]: isOrganisation,
  };
};

const serialiseCounsel = (counsel, linkKey, validIds) => ({
  name: counsel.name,
  law_firm_name: counsel.law_firm_name || "",
  email: counsel.email,
  phone: counsel.phone,
  bar_enrollment: counsel.bar_enrollment,
  // Drop links to parties that were removed after the counsel was mapped.
  [linkKey]: (counsel[linkKey] || []).filter((id) => validIds.includes(id)),
});

export const buildArbitrationPayload = (values, countries = []) => {
  const claimants = values.claimants || [];
  const respondents = values.respondents || [];
  const claimantIds = claimants.map((c) => c.id);
  const respondentIds = respondents.map((r) => r.id);

  return {
    claimant: claimants.map((c) => serialiseParty(c, "is_claimant_organisation", countries)),
    respondent: respondents.map((r) => serialiseParty(r, "is_respondent_organisation", countries)),
    claimant_counsel: (values.claimant_counsels || []).map((c) =>
      serialiseCounsel(c, "claimant_ids", claimantIds)
    ),
    respondent_counsel: (values.respondent_counsels || []).map((c) =>
      serialiseCounsel(c, "respondent_ids", respondentIds)
    ),
  };
};

export const submitArbitrationRequest = async (values, files = [], countries = []) => {
  try {
    const formData = new FormData();

    // 1. Map complex structures to singular keys expected by the backend
    const payload = buildArbitrationPayload(values, countries);
    formData.append("claimant", JSON.stringify(payload.claimant));
    formData.append("respondent", JSON.stringify(payload.respondent));
    formData.append("claimant_counsel", JSON.stringify(payload.claimant_counsel));
    formData.append("respondent_counsel", JSON.stringify(payload.respondent_counsel));

    // 2. Map flat fields and format strings with literal quotes to match backend requirements
    formData.append("category", `"${values.case_category || ""}"`);

    // Determine number value depending on selected case type
    let numVal = "";
    // Only CMP and Reference carry a reference number; the other categories
    // (Institutional, Other) send an empty one.
    if (values.case_category === CASE_CATEGORIES.CMP) numVal = values.cmp_number;
    if (values.case_category === CASE_CATEGORIES.REFERENCE) numVal = values.msed_number;
    formData.append("category_type_number", `"${numVal || ""}"`);

    formData.append(
      "region",
      values.case_category === CASE_CATEGORIES.REFERENCE ? values.region || "" : ""
    );

    // 3. Append uploaded files under the "documents" key
    files.forEach((file) => {
      formData.append("documents", file);
    });

    // 4. Send the Request
    const response = await instance('bac/register-case/', {
      method: "POST",
      data: formData,
      params: {
        host: getPlatform(),
      },
      suppressGlobalErrorHandling: true,
    });

    console.log('res', response);

    // ── Success ────────────────────────────────────────────────────────────
    if (response.status === 200) {
      const data = response.data || {};
      return {
        success: true,
        referenceId: data.referenceId || data.reference_id || null,
      };
    }

    return {
      success: false,
      globalError:
        "An unexpected error occurred while submitting your request. Please try again in a few moments.",
    };
  } catch (networkError) {
    console.error("[ArbitrationAPI] Network error:", networkError);

    const status = networkError?.response?.status;
    const data = networkError?.response?.data || {};

    // ── 409 Conflict — duplicate email / phone ───────────────────────────
    if (status === 409) {
      const fieldErrors = {};
      const conflicts = data.conflicts || data.fields || [];

      if (Array.isArray(conflicts) && conflicts.length > 0) {
        conflicts.forEach((conflictKey) => {
          const formField = CONFLICT_FIELD_MAP[conflictKey];
          if (formField) {
            fieldErrors[formField] =
              CONFLICT_MESSAGES[formField] ||
              "This value already exists in our records.";
          }
        });
      }

      if (Object.keys(fieldErrors).length === 0 && data.field) {
        const formField = CONFLICT_FIELD_MAP[data.field];
        if (formField) {
          fieldErrors[formField] =
            CONFLICT_MESSAGES[formField] ||
            "This value already exists in our records.";
        }
      }

      if (Object.keys(fieldErrors).length > 0) {
        return { success: false, fieldErrors };
      }

      return {
        success: false,
        globalError:
          data.message ||
          "A record with the provided email or phone number already exists. Please review your details.",
      };
    }

    // ── 400 Validation Error ─────────────────────────────────────────────
    if (status === 400) {
      return {
        success: false,
        globalError:
          data?.message ||
          data?.error ||
          data?.globalError ||
          "Some fields could not be validated by the server. Please review your entries.",
      };
    }

    return {
      success: false,
      globalError:
        data.message ||
        data.error ||
        networkError?.message ||
        "Unable to reach the server. Please check your internet connection and try again.",
    };
  }
};

// ─────────────────────────────────────────────────────────────────────────────
// Main Page
// ─────────────────────────────────────────────────────────────────────────────
const CreateNewMatter = () => {
  const classes = useStyles();

  const [flatValues, setFlatValues] = useState({
    case_category: "",
    cmp_number: "",
    msed_number: "",
    region: "",
  });

  const { cities: referringCities } = useLocations(
    REFERRING_COUNTRY_CODE,
    REFERRING_STATE_CODE
  );
  const categorySection = useMemo(
    () => ({
      ...caseCategorySection,
      fields: caseCategorySection.fields.map((field) =>
        field.id === "region"
          ? {
              ...field,
              options: (referringCities || []).map((city) => ({
                value: city.name,
                label: city.name,
              })),
            }
          : field
      ),
    }),
    [referringCities]
  );
  const [flatErrors, setFlatErrors] = useState({});

  const [claimants, setClaimants] = useState(() => [blankClaimant()]);
  const [claimantCounsels, setClaimantCounsels] = useState(() => [blankClaimantCounsel()]);
  const [respondents, setRespondents] = useState(() => [blankRespondent()]);
  const [respondentCounsels, setRespondentCounsels] = useState(() => [blankRespondentCounsel()]);

  const [partyErrors, setPartyErrors] = useState({
    claimants: [{}],
    claimantCounsels: [{}],
    respondents: [{}],
    respondentCounsels: [{}],
  });

  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [autofillLoading, setAutofillLoading] = useState(false);
  const [autofillFile, setAutofillFile] = useState(null);
  const [autofillStatus, setAutofillStatus] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [referenceId, setReferenceId] = useState(null);
  const [globalError, setGlobalError] = useState("");
  const countries = useCountries();

  const countryOptions = useMemo(
    () =>
      // Alphabetical, with India first — it is the common case for ACCB.
      [...countries]
        .sort((a, b) => {
          const nameA = (a.name || "").toUpperCase();
          const nameB = (b.name || "").toUpperCase();
          if (nameA === nameB) return 0;
          if (nameA === "INDIA") return -1;
          if (nameB === "INDIA") return 1;
          return nameA < nameB ? -1 : 1;
        })
        .map((c) => ({ value: c.id, label: c.name })),
    [countries]
  );

  // ── flat handlers ─────────────────────────────────────────────────────────
  const handleFlatChange = useCallback((id, val) => {
    setFlatValues((prev) => ({ ...prev, [id]: val }));
    setFlatErrors((prev) => ({ ...prev, [id]: "" }));
  }, []);

  // ── party handlers factory ────────────────────────────────────────────────
  const makePartyHandlers = (setter, errKey) => ({
    onAdd: (blankFn) => {
      setter((prev) => [...prev, blankFn()]);
      setPartyErrors((e) => ({ ...e, [errKey]: [...(e[errKey] || []), {}] }));
    },
    onRemove: (idx) => {
      setter((prev) => prev.filter((_, i) => i !== idx));
      setPartyErrors((e) => ({ ...e, [errKey]: (e[errKey] || []).filter((_, i) => i !== idx) }));
    },
    onChange: (idx, key, val) => {
      setter((prev) => prev.map((p, i) => (i === idx ? { ...p, [key]: val } : p)));
      setPartyErrors((e) => ({
        ...e,
        [errKey]: (e[errKey] || []).map((err, i) => (i === idx ? { ...err, [key]: "" } : err)),
      }));
    },
  });

  const claimantH = makePartyHandlers(setClaimants, "claimants");
  const claimantCounselH = makePartyHandlers(setClaimantCounsels, "claimantCounsels");
  const respondentH = makePartyHandlers(setRespondents, "respondents");
  const respondentCounselH = makePartyHandlers(setRespondentCounsels, "respondentCounsels");

  const claimantOptions = useMemo(() => buildPartyOptions(claimants, "Claimant"), [claimants]);
  const respondentOptions = useMemo(() => buildPartyOptions(respondents, "Respondent"), [respondents]);

  const removeClaimant = (idx) => {
    const removedId = claimants[idx] && claimants[idx].id;
    claimantH.onRemove(idx);
    setClaimantCounsels((prev) =>
      prev.map((c) => ({
        ...c,
        claimant_ids: (c.claimant_ids || []).filter((id) => id !== removedId),
      }))
    );
  };

  const removeRespondent = (idx) => {
    const removedId = respondents[idx] && respondents[idx].id;
    respondentH.onRemove(idx);
    setRespondentCounsels((prev) =>
      prev.map((c) => ({
        ...c,
        respondent_ids: (c.respondent_ids || []).filter((id) => id !== removedId),
      }))
    );
  };

  // ── validation ────────────────────────────────────────────────────────────
  const validate = () => {
    let valid = true;
    const fe = {};

    // Validate Case Category Section
    if (!flatValues.case_category) {
      fe.case_category = "Select Category is required";
      valid = false;
    }

    if (flatValues.case_category === CASE_CATEGORIES?.CMP && (!flatValues.cmp_number || !flatValues.cmp_number.trim())) {
      fe.cmp_number = "CMP Number is required";
      valid = false;
    }
    if (flatValues.case_category === CASE_CATEGORIES?.REFERENCE && (!flatValues.msed_number || !flatValues.msed_number.trim())) {
      fe.msed_number = "MSEFC Number is required";
      valid = false;
    }

    setFlatErrors(fe);

    // Validate Multi-Party sections (Claimants & Respondents)
    const validateParties = (parties, rules, errKey) => {
      const errs = parties.map((p) => {
        const pErr = {};
        rules.forEach(({ key, label, type, required: req }) => {
          let e = "";
          if (type === "email") {
            if (req && (!p[key] || !p[key].toString().trim())) e = `${label} is required`;
            else e = validateEmail(p[key]);
          } else if (type === "tel") e = validateMobile(p[key], false);
          else e = req && validateRequired(p[key], label);
          if (e) { pErr[key] = e; valid = false; }
        });
        return pErr;
      });
      setPartyErrors((prev) => ({ ...prev, [errKey]: errs }));
    };

    const claimantRules = [
      { key: "name", label: "Claimant Name" },
      { key: "email", label: "Email ID", type: "email" },
      { key: "mobile", label: "Phone Number", type: "tel" },
      { key: "address", label: "Address (1st line)" },
      { key: "city", label: "City" },
      { key: "state", label: "State" },
      { key: "pincode", label: "Pin Code" },
      { key: "country", label: "Country", required: true },
    ];
    const respondentRules = [
      { key: "name", label: "Respondent Name" },
      { key: "email", label: "Email ID", type: "email" },
      { key: "mobile", label: "Phone Number", type: "tel" },
      { key: "address", label: "Address (1st line)" },
      { key: "city", label: "City" },
      { key: "state", label: "State" },
      { key: "pincode", label: "Pin Code" },
      { key: "country", label: "Country", required: true },
    ];

    validateParties(claimants, claimantRules, "claimants");
    validateParties(respondents, respondentRules, "respondents");

    const validateCounsels = (counsels, linkKey, partyLabel, errKey) => {
      const errs = counsels.map((c) => {
        const cErr = {};
        const hasDetails = ["name", "email", "phone", "bar_enrollment"].some(
          (k) => c[k] && c[k].toString().trim()
        );
        const emailErr = validateEmail(c.email);
        if (emailErr) { cErr.email = emailErr; valid = false; }
        if (hasDetails && (c[linkKey] || []).length === 0) {
          cErr[linkKey] = `Select at least one ${partyLabel} this counsel represents`;
          valid = false;
        }
        return cErr;
      });
      setPartyErrors((prev) => ({ ...prev, [errKey]: errs }));
    };

    validateCounsels(claimantCounsels, "claimant_ids", "claimant", "claimantCounsels");
    validateCounsels(respondentCounsels, "respondent_ids", "respondent", "respondentCounsels");

    return valid;
  };

  // ── submit ────────────────────────────────────────────────────────────────
  const handleSubmit = useCallback(async () => {
    setGlobalError("");
    if (!validate()) {
      setGlobalError("Please fix validation errors across required fields in the form before submission.");
      return;
    }
    setSubmitting(true);

    const payload = {
      ...flatValues,
      claimants,
      claimant_counsels: claimantCounsels,
      respondents,
      respondent_counsels: respondentCounsels,
    };

    try {
      const result = await submitArbitrationRequest(payload, uploadedFiles, countries);
      if (result.success) {
        setReferenceId(result.referenceId);
        setSubmitted(true);
      } else {
        setGlobalError(result.globalError || "Submission failed. Please try again.");
      }
    } catch (err) {
      setGlobalError("An unexpected server error occurred. Please try again.");
    } finally {
      setSubmitting(false);
    }
  }, [flatValues, claimants, claimantCounsels, respondents, respondentCounsels, uploadedFiles, countries]);

  // ── Auto-fill ─────────────────────────────────────────────────────────────
  // Take the AI-extracted party details and prefill the whole form. Nothing is
  // submitted — the admin reviews/edits before submitting as usual.
  const applyAutofillData = (data) => {
    setFlatValues((prev) => ({
      ...prev,
      case_category: data.case_category || prev.case_category || "",
      cmp_number: data.cmp_number || prev.cmp_number || "",
    }));

    const countryIdByName = (name) => {
      if (!name) return "";
      const hit = countries.find(
        (c) => (c.name || "").toLowerCase() === String(name).toLowerCase()
      );
      return hit ? hit.id : "";
    };

    const mapParty = (p, orgKey, blankFn) => ({
      ...blankFn(),
      name: p.name || "",
      [orgKey]:
        p.is_organisation === true ? "true"
          : p.is_organisation === false ? "false" : "",
      representative_name: p.representative_name || "",
      email: p.email || "",
      mobile: p.mobile || "",
      address: p.address || "",
      city: p.city || "",
      state: p.state || "",
      pincode: p.pincode || "",
      country: countryIdByName(p.country),
    });

    const newClaimants = (data.claimants || []).map((p) =>
      mapParty(p, "is_claimant_organisation", blankClaimant));
    const newRespondents = (data.respondents || []).map((p) =>
      mapParty(p, "is_respondent_organisation", blankRespondent));

    const finalClaimants = newClaimants.length ? newClaimants : [blankClaimant()];
    const finalRespondents = newRespondents.length ? newRespondents : [blankRespondent()];
    setClaimants(finalClaimants);
    setRespondents(finalRespondents);

    const claimantIds = finalClaimants.map((c) => c.id);
    const respondentIds = finalRespondents.map((r) => r.id);

    // `represents` comes back as 1-based positions in the party list on that
    // side. When it is missing or unusable, fall back to every party — a
    // cause-title counsel represents the whole side (the admin can narrow it).
    const resolveLinks = (represents, ids) => {
      if (!Array.isArray(represents) || represents.length === 0) return ids;
      const linked = represents.map((n) => ids[Number(n) - 1]).filter(Boolean);
      return linked.length ? linked : ids;
    };

    const mapCounsel = (c, linkKey, ids, blankFn) => ({
      ...blankFn(),
      name: c.name || "",
      email: c.email || "",
      phone: c.phone || "",
      bar_enrollment: c.bar_enrollment || "",
      [linkKey]: resolveLinks(c.represents, ids),
    });

    // The extraction has used both the plural and the singular key for these
    // lists, so read whichever one arrived instead of dropping the section.
    const counselsFrom = (...keys) => {
      const key = keys.find((k) => Array.isArray(data[k]) && data[k].length);
      return key ? data[key] : [];
    };
    // Keep a counsel that carries any detail — a name-less row with a phone or
    // bar number is still worth showing to the admin.
    const hasCounselDetails = (c) =>
      Boolean(c) &&
      ["name", "email", "phone", "bar_enrollment"].some(
        (k) => c[k] && c[k].toString().trim()
      );

    const newClaimantCounsels = counselsFrom("claimant_counsels", "claimant_counsel")
      .filter(hasCounselDetails)
      .map((c) => mapCounsel(c, "claimant_ids", claimantIds, blankClaimantCounsel));
    const newRespondentCounsels = counselsFrom("respondent_counsels", "respondent_counsel")
      .filter(hasCounselDetails)
      .map((c) => mapCounsel(c, "respondent_ids", respondentIds, blankRespondentCounsel));

    const finalClaimantCounsels = newClaimantCounsels.length ? newClaimantCounsels : [blankClaimantCounsel()];
    const finalRespondentCounsels = newRespondentCounsels.length ? newRespondentCounsels : [blankRespondentCounsel()];
    setClaimantCounsels(finalClaimantCounsels);
    setRespondentCounsels(finalRespondentCounsels);

    // Keep the per-row error arrays the same length as the new party lists.
    setPartyErrors({
      claimants: finalClaimants.map(() => ({})),
      claimantCounsels: finalClaimantCounsels.map(() => ({})),
      respondents: finalRespondents.map(() => ({})),
      respondentCounsels: finalRespondentCounsels.map(() => ({})),
    });

    return {
      claimantCounsels: newClaimantCounsels.length,
      respondentCounsels: newRespondentCounsels.length,
    };
  };

  // The shared uploader is multi-file; auto-fill reads a single document, so keep
  // only the latest selection.
  const handleAutofillFileChange = useCallback((files) => {
    setAutofillFile(files.length ? files[files.length - 1] : null);
    setAutofillStatus(null);
  }, []);

  const handleAutofill = async (file) => {
    // The button stays enabled so it reads as the live next step rather than a
    // dead grey block; tell the user what is missing instead of going quiet.
    if (!file) {
      setAutofillStatus({
        error: true,
        message: `Choose a ${AUTOFILL_SOURCE.label} ${AUTOFILL_SOURCE.documentName} above, then click Auto-fill.`,
      });
      return;
    }
    setGlobalError("");
    setAutofillStatus(null);
    setAutofillLoading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await instance(AUTOFILL_SOURCE.endpoint, {
        method: "POST",
        data: fd,
        params: { host: getPlatform() },
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (res.status === 200 && res.data && res.data.success) {
        const filled = applyAutofillData(res.data);
        // Counsel details are often absent from the cause title; say so instead
        // of leaving the admin wondering why those sections stayed empty.
        const missing = [
          filled.claimantCounsels ? "" : "claimant counsel",
          filled.respondentCounsels ? "" : "respondent counsel",
        ].filter(Boolean);
        setAutofillStatus({
          error: false,
          message:
            `Form auto-filled from the ${AUTOFILL_SOURCE.label} document. Please review every field before submitting.` +
            (missing.length
              ? ` No ${missing.join(" or ")} details were found in this document — please enter them manually.`
              : ""),
        });
      } else {
        setAutofillStatus({
          error: true,
          message: (res.data && res.data.error) || "Couldn't read this document. Please fill the form manually.",
        });
      }
    } catch (err) {
      setAutofillStatus({
        error: true,
        message: err?.response?.data?.error || "Couldn't read this document. Please fill the form manually.",
      });
    } finally {
      setAutofillLoading(false);
    }
  };

  // ── thank you ─────────────────────────────────────────────────────────────
  if (submitted) {
    return (
      <Box className={classes.page}>
        <Container className={classes.container}>
          <Paper className={classes.paper}>
            <ThankYouACCB referenceId={referenceId} />
          </Paper>
        </Container>
      </Box>
    );
  }

  const docMeta = getDocumentUploadMeta(flatValues.case_category);
  const dynamicDocField = { ...documentUploadField, label: docMeta?.label || "Upload", helperText: docMeta?.helperText || "" };

  let sectionNo = 0;
  const next = () => ++sectionNo;

  return (
    <Box className={classes.page}>
      <Container className={classes.container}>
        <Paper className={classes.paper}>

          {/* Header */}
          <Box className={classes.formHeader}>
            {/* <Typography className={classes.formLabel}>ACCB — Online Dispute Resolution</Typography> */}
            <Typography className={classes.formSubtitle}>
              Please complete all required fields marked with an asterisk (*). The information provided
              will be used solely for the purpose of facilitating the arbitration process.
            </Typography>
          </Box>

          <Box className={classes.formBody}>

            {/* Auto-fill from a source document */}
            <Box className={classes.autofillCard}>
              <Typography className={classes.autofillTitle}>
                Auto-fill from {AUTOFILL_SOURCE.label}
              </Typography>
              <Typography className={classes.autofillSubtitle}>
                Upload the {AUTOFILL_SOURCE.label} {AUTOFILL_SOURCE.documentName} (PDF), then click
                Auto-fill. We'll read the parties, counsels and addresses to prefill this form —
                please review and edit everything before submitting.
              </Typography>

              <DocumentUpload
                field={AUTOFILL_UPLOAD_FIELD}
                files={autofillFile ? [autofillFile] : []}
                onFilesChange={handleAutofillFileChange}
              />

              {autofillStatus && (
                <Typography
                  className={`${classes.autofillStatus} ${autofillStatus.error ? classes.autofillStatusError : classes.autofillStatusSuccess
                    }`}
                >
                  {autofillStatus.message}
                </Typography>
              )}

              {/* Only disabled while a request is in flight. Disabling it until a
                  file was picked left MUI's grey contained button sitting on the
                  pale lilac card, which read as a non-interactive background
                  element rather than the section's primary action. */}
              <Box display="flex" justifyContent="center" >
                <Button
                  variant="contained"
                  color="primary"
                  className={classes.autofillButton}
                  disabled={autofillLoading}
                  startIcon={autofillLoading ? <CircularProgress size={16} color="inherit" /> : null}
                  onClick={() => handleAutofill(autofillFile)}
                  disableElevation
                >
                  {autofillLoading ? "Reading document…" : "Auto-fill"}
                </Button>
              </Box>
            </Box>

            {/* 01 — Case Category */}
            <StaticSection sectionNumber={next()} section={categorySection} values={flatValues} onChange={handleFlatChange} errors={flatErrors} />
            <Divider className={classes.sectionDivider} />

            {/* 02 — Claimant Details */}
            <MultiPartySection
              sectionNumber={next()}
              sectionTitle="Claimant Details"
              sectionSubtitle="Information about the party / parties initiating the arbitration"
              partyLabel="Claimant"
              addLabel="Add Another Claimant"
              parties={claimants}
              fieldTemplate={claimantFieldTemplate}
              templateOptions={countryOptions}
              onAdd={() => claimantH.onAdd(blankClaimant)}
              onRemove={removeClaimant}
              onChange={claimantH.onChange}
              errors={partyErrors.claimants}
            />
            <Divider className={classes.sectionDivider} />

            {/* 03 — Claimant Counsel */}
            <MultiPartySection
              sectionNumber={next()}
              sectionTitle="Claimant Counsel Details"
              sectionSubtitle="Legal representative(s) for the claimant, if any. A counsel can be mapped to one or more claimants."
              partyLabel="Claimant Counsel"
              addLabel="Add Another Claimant Counsel"
              parties={claimantCounsels}
              fieldTemplate={claimantCounselFieldTemplate}
              templateOptions={claimantOptions}
              onAdd={() => claimantCounselH.onAdd(blankClaimantCounsel)}
              onRemove={claimantCounselH.onRemove}
              onChange={claimantCounselH.onChange}
              errors={partyErrors.claimantCounsels}
            />
            <Divider className={classes.sectionDivider} />

            {/* 04 — Respondent Details */}
            <MultiPartySection
              sectionNumber={next()}
              sectionTitle="Respondent Details"
              sectionSubtitle="Information about the opposing party / parties in the dispute"
              partyLabel="Respondent"
              addLabel="Add Another Respondent"
              parties={respondents}
              fieldTemplate={respondentFieldTemplate}
              templateOptions={countryOptions}
              onAdd={() => respondentH.onAdd(blankRespondent)}
              onRemove={removeRespondent}
              onChange={respondentH.onChange}
              errors={partyErrors.respondents}
            />
            <Divider className={classes.sectionDivider} />

            {/* 05 — Respondent Counsel */}
            <MultiPartySection
              sectionNumber={next()}
              sectionTitle="Respondent Counsel Details"
              sectionSubtitle="Legal representative(s) for the respondent, if any. A counsel can be mapped to one or more respondents."
              partyLabel="Respondent Counsel"
              addLabel="Add Another Respondent Counsel"
              parties={respondentCounsels}
              fieldTemplate={respondentCounselFieldTemplate}
              templateOptions={respondentOptions}
              onAdd={() => respondentCounselH.onAdd(blankRespondentCounsel)}
              onRemove={respondentCounselH.onRemove}
              onChange={respondentCounselH.onChange}
              errors={partyErrors.respondentCounsels}
            />
            <Divider className={classes.sectionDivider} />

            {/* 06 — Document Upload */}
            <Box className={classes.sectionWrapper}>
              <Box className={classes.sectionHeader}>
                <Typography className={classes.sectionIndex}>{String(next()).padStart(2, "0")}</Typography>
                <Typography variant="h6" className={classes.sectionTitle}>Document Upload</Typography>
                <Typography className={classes.sectionSubtitle}>{docMeta?.helperText}</Typography>
              </Box>
              <Divider className={classes.sectionHr} />
              <DocumentUpload field={dynamicDocField} files={uploadedFiles} onFilesChange={setUploadedFiles} />
            </Box>
            <Divider className={classes.sectionDivider} />

            {/* Global error */}
            <Collapse in={Boolean(globalError)}>
              {globalError && (
                <Box className={classes.globalError} mt={3}>
                  <ErrorOutlineIcon className={classes.globalErrorIcon} />
                  <Typography className={classes.globalErrorText}>{globalError}</Typography>
                </Box>
              )}
            </Collapse>
          </Box>

          {/* Footer */}
          <Box className={classes.footer}>
            <Typography className={classes.requiredNote}>Fields marked with * are required</Typography>
            <Button
              variant="contained"
              color="primary"
              disabled={submitting}
              className={classes.submitButton}
              disableElevation
              onClick={handleSubmit}
            >
              {submitting ? (
                <>
                  Submitting…
                  <CircularProgress size={20} className={classes.buttonProgress} color="inherit" />
                </>
              ) : (
                "Submit Request"
              )}
            </Button>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default CreateNewMatter;